// src/views/Responders/ResponderShifts.js
//
// Transit Watch Console - Responder Shift Log
//
// Displays responder_shift_sessions for the currently selected agency.
// V1 is read-only.
//
// The backend currently returns the newest 250 shift sessions.
// Filtering on this page therefore applies to those loaded records.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Globals from "../../utils/globals";
import "./ResponderShifts.css";


/*
 * Safely display an ordinary text value.
 */
function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return value;
}


/*
 * Format a Mongo/ISO date for the Console user's local timezone.
 */
function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString();
}


/*
 * Date only, used in the first table column.
 */
function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString();
}


/*
 * Time only, used for Time On / Time Off.
 */
function formatTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}


/*
 * Calculate elapsed shift duration.
 *
 * Active shifts use the current time so supervisors can see
 * approximately how long the responder has been on duty.
 */
function formatDuration(startedAt, endedAt) {
  if (!startedAt) return "—";

  const start = new Date(startedAt).getTime();
  const end = endedAt
    ? new Date(endedAt).getTime()
    : Date.now();

  if (
    Number.isNaN(start) ||
    Number.isNaN(end) ||
    end < start
  ) {
    return "—";
  }

  const totalMinutes = Math.floor(
    (end - start) / 60000
  );

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  }

  return `${hours}h ${minutes}m`;
}


/*
 * Calculate vehicle miles driven during the shift.
 */
function calculateMiles(startMileage, endMileage) {
  if (
    startMileage === null ||
    startMileage === undefined ||
    endMileage === null ||
    endMileage === undefined
  ) {
    return "—";
  }

  const start = Number(startMileage);
  const end = Number(endMileage);

  if (
    Number.isNaN(start) ||
    Number.isNaN(end)
  ) {
    return "—";
  }

  return end - start;
}


/*
 * Human-readable guard name.
 */
function guardName(shift) {
  const name = [
    shift.first_name,
    shift.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return name || "—";
}


/*
 * CSV helper.
 *
 * Excel opens CSV files directly, so this gives supervisors
 * an immediate export without adding another dependency.
 */
function csvEscape(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  const text = String(value);

  return `"${text.replace(/"/g, '""')}"`;
}


export default function ResponderShifts({
  agencyId,
}) {
  const { getAccessTokenSilently } = useAuth0();

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [guardFilter, setGuardFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftPhotoUrls, setShiftPhotoUrls] = useState({});
  const [shiftPhotosLoading, setShiftPhotosLoading] = useState(false);
  const [shiftPhotoError, setShiftPhotoError] = useState("");
  const [largeShiftPhotoUrl, setLargeShiftPhotoUrl] = useState(null);

  /*
   * Load the newest responder shift sessions.
   */
  const loadShifts = useCallback(async () => {
    if (!agencyId) {
      setShifts([]);
      return;
    }

    setLoading(true);
    setLoadError("");

    try {
      const token =
        await getAccessTokenSilently();

      const response = await fetch(
       `${Globals.apiEndpoint}/dashboard_responders/agencies/${agencyId}/shifts`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message ||
            "Unable to load responder shifts."
        );
      }

      setShifts(
        Array.isArray(data.shifts)
          ? data.shifts
          : []
      );
    } catch (error) {
      console.error(
        "Responder shifts load failed:",
        error
      );

      setLoadError(
        error.message ||
          "Unable to load responder shifts."
      );

      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, [
    agencyId,
    getAccessTokenSilently,
  ]);


  useEffect(() => {
    loadShifts();
  }, [loadShifts]);


  /*
   * Unique filter options from the records currently loaded.
   */
  const unitOptions = useMemo(() => {
    return [
      ...new Set(
        shifts
          .map((shift) => shift.unit_name)
          .filter(Boolean)
      ),
    ].sort();
  }, [shifts]);


  const shiftOptions = useMemo(() => {
    return [
      ...new Set(
        shifts
          .map((shift) => shift.shift_name)
          .filter(Boolean)
      ),
    ].sort();
  }, [shifts]);


  /*
   * Apply all local V1 filters.
   */
  const filteredShifts = useMemo(() => {
    const guardSearch =
      guardFilter.trim().toLowerCase();

    return shifts.filter((shift) => {
      /*
       * Guard name / ID search
       */
      if (guardSearch) {
        const searchable = [
          shift.first_name,
          shift.last_name,
          shift.guard_id,
          shift.partner_name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(guardSearch)) {
          return false;
        }
      }


      /*
       * Unit
       */
      if (
        unitFilter &&
        shift.unit_name !== unitFilter
      ) {
        return false;
      }


      /*
       * Shift
       */
      if (
        shiftFilter &&
        shift.shift_name !== shiftFilter
      ) {
        return false;
      }


      /*
       * Active / Ended
       */
      if (
        statusFilter &&
        shift.status !== statusFilter
      ) {
        return false;
      }


      /*
       * Date range is based on shift start.
       */
      if (startDate && shift.started_at) {
        const shiftDate =
          new Date(shift.started_at);

        const start =
          new Date(`${startDate}T00:00:00`);

        if (shiftDate < start) {
          return false;
        }
      }


      if (endDate && shift.started_at) {
        const shiftDate =
          new Date(shift.started_at);

        const end =
          new Date(`${endDate}T23:59:59.999`);

        if (shiftDate > end) {
          return false;
        }
      }

      return true;
    });
  }, [
    shifts,
    startDate,
    endDate,
    guardFilter,
    unitFilter,
    shiftFilter,
    statusFilter,
  ]);


  function clearFilters() {
    setStartDate("");
    setEndDate("");
    setGuardFilter("");
    setUnitFilter("");
    setShiftFilter("");
    setStatusFilter("");
  }


  /*
   * Export exactly the records currently visible after filtering.
   */
  function exportCSV() {
    if (filteredShifts.length === 0) {
      return;
    }

    const headers = [
      "Date",
      "Guard Name",
      "Guard ID",
      "Department",
      "Unit",
      "Shift",
      "Partner",
      "Time On",
      "Time Off",
      "Duration",
      "Status",
      "Vehicle",
      "Start Mileage",
      "End Mileage",
      "Miles Driven",
      "Start Fuel",
      "End Fuel",
      "Start Vehicle Condition",
      "End Vehicle Condition",
      "Vehicle Check Incident",
    ];

    const rows = filteredShifts.map(
      (shift) => [
        formatDate(shift.started_at),
        guardName(shift),
        shift.guard_id,
        shift.department_name,
        shift.unit_name,
        shift.shift_name,
        shift.partner_name,
        formatDateTime(shift.started_at),
        formatDateTime(shift.ended_at),
        formatDuration(
          shift.started_at,
          shift.ended_at
        ),
        shift.status,
        shift.vehicle_number,
        shift.start_mileage,
        shift.end_mileage,
        calculateMiles(
          shift.start_mileage,
          shift.end_mileage
        ),
        shift.start_fuel_level,
        shift.end_fuel_level,
        shift.start_vehicle_condition,
        shift.end_vehicle_condition,
        shift.vehicle_check_display_title,
      ]
    );

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((row) =>
        row.map(csvEscape).join(",")
      ),
    ].join("\n");

    const blob = new Blob(
      [csv],
      {
        type: "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    const today =
      new Date()
        .toISOString()
        .slice(0, 10);

    link.href = url;
    link.download =
      `responder-shifts-${today}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  /*
 * LOAD SECURE RESPONDER SHIFT PHOTOS
 *
 * Called when View Details is opened for a responder shift.
 *
 * The photos remain private in Media 2026 S3.
 * For each START SHIFT / END SHIFT photo, the Console asks the
 * dashboard responder API for a temporary signed viewing URL.
 *
 * The backend verifies:
 * - Auth0 Console authentication
 * - agency permission
 * - shift session ID
 * - that the requested object_key actually belongs to this shift
 */
const loadShiftPhotos = useCallback(
  async (shift) => {
    if (!shift?._id || !agencyId) {
      return;
    }

    const photos = [
      ...(shift.photos_start || []),
      ...(shift.photos_end || []),
    ];

    if (photos.length === 0) {
      setShiftPhotoUrls({});
      setShiftPhotoError("");
      return;
    }

    setShiftPhotosLoading(true);
    setShiftPhotoError("");
    setShiftPhotoUrls({});

    try {
      const token =
        await getAccessTokenSilently();

      const photoUrlEntries =
        await Promise.all(
          photos.map(async (photo) => {
            const objectKey =
              photo.thumbnail_object_key ||
              photo.object_key;

            if (!objectKey) {
              return null;
            }

            const response = await fetch(
              `${Globals.apiEndpoint}/dashboard_responders/agencies/${agencyId}/shifts/${shift._id}/media/signed-view`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type":
                    "application/json",
                },
                body: JSON.stringify({
                  object_key: objectKey,
                }),
              }
            );

            const data = await response.json();

            if (!response.ok) {
              throw new Error(
                data?.message ||
                  "Unable to load shift photo."
              );
            }

            return [
              photo.object_key ||
                objectKey,
              data.view_url,
            ];
          })
        );

      const urlMap = {};

      photoUrlEntries.forEach((entry) => {
        if (entry) {
          urlMap[entry[0]] = entry[1];
        }
      });

      setShiftPhotoUrls(urlMap);
    } catch (error) {
      console.error(
        "Unable to load responder shift photos:",
        error
      );

      setShiftPhotoError(
        error.message ||
          "Unable to load responder shift photos."
      );
    } finally {
      setShiftPhotosLoading(false);
    }
  },
  [
    agencyId,
    getAccessTokenSilently,
  ]
);

  return (
    <div
      className="responder-shifts-view"
      style={{
        paddingTop: "16px",
        paddingBottom: "30px",
      }}
    >
      {/* HEADER */}
      <div
        className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3"
      >
        <div>
          <h2 className="mb-1">
            Responder Shifts
          </h2>

          <div className="text-muted">
            Responder shift and vehicle activity log
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={loadShifts}
            disabled={loading}
          >
            {loading
              ? "Refreshing..."
              : "Refresh"}
          </button>

          <button
            type="button"
            className="btn btn-success"
            onClick={exportCSV}
            disabled={
              filteredShifts.length === 0
            }
          >
            Export CSV
          </button>
        </div>
      </div>


      {/* FILTERS */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label">
                From
              </label>

              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(event) =>
                  setStartDate(
                    event.target.value
                  )
                }
              />
            </div>


            <div className="col-md-2">
              <label className="form-label">
                To
              </label>

              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={(event) =>
                  setEndDate(
                    event.target.value
                  )
                }
              />
            </div>


            <div className="col-md-2">
              <label className="form-label">
                Guard
              </label>

              <input
                type="text"
                className="form-control"
                placeholder="Name or ID"
                value={guardFilter}
                onChange={(event) =>
                  setGuardFilter(
                    event.target.value
                  )
                }
              />
            </div>


            <div className="col-md-2">
              <label className="form-label">
                Unit
              </label>

              <select
                className="form-select"
                value={unitFilter}
                onChange={(event) =>
                  setUnitFilter(
                    event.target.value
                  )
                }
              >
                <option value="">
                  All Units
                </option>

                {unitOptions.map((unit) => (
                  <option
                    key={unit}
                    value={unit}
                  >
                    {unit}
                  </option>
                ))}
              </select>
            </div>


            <div className="col-md-2">
              <label className="form-label">
                Shift
              </label>

              <select
                className="form-select"
                value={shiftFilter}
                onChange={(event) =>
                  setShiftFilter(
                    event.target.value
                  )
                }
              >
                <option value="">
                  All Shifts
                </option>

                {shiftOptions.map(
                  (shiftName) => (
                    <option
                      key={shiftName}
                      value={shiftName}
                    >
                      {shiftName}
                    </option>
                  )
                )}
              </select>
            </div>


            <div className="col-md-2">
              <label className="form-label">
                Status
              </label>

              <select
                className="form-select"
                value={statusFilter}
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >
                <option value="">
                  All Statuses
                </option>

                <option value="active">
                  Active
                </option>

                <option value="ended">
                  Ended
                </option>
              </select>
            </div>
          </div>


          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div>
              <strong>
                {filteredShifts.length}
              </strong>{" "}
              shift
              {filteredShifts.length === 1
                ? ""
                : "s"}{" "}
              displayed
            </div>

            <button
              type="button"
              className="btn btn-link"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>


      {/* ERROR */}
      {loadError && (
        <div
          className="alert alert-danger"
          role="alert"
        >
          {loadError}
        </div>
      )}


      {/* LOADING */}
      {loading && shifts.length === 0 && (
        <div
          className="text-center py-5"
        >
          Loading responder shifts...
        </div>
      )}


      {/* EMPTY */}
      {!loading &&
        !loadError &&
        filteredShifts.length === 0 && (
          <div
            className="alert alert-light border"
          >
            No responder shift sessions match
            the current filters.
          </div>
        )}


      {/* SHIFT TABLE */}
      {filteredShifts.length > 0 && (
        <div className="table-responsive">
          <table
            className="table table-striped table-hover table-bordered align-middle responder-shifts-table"
          >
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Guard</th>
                <th>Guard ID</th>
                <th>Dept</th>
                <th>Unit</th>
                <th>Shift</th>
                <th>Time On</th>
                <th>Time Off</th>
                <th>Duration</th>
                <th>Vehicle</th>
                <th>Start Miles</th>
                <th>End Miles</th>
                <th>Miles</th>
                <th>Start Fuel</th>
                <th>End Fuel</th>
                <th>Status</th>
                <th>Details</th>
              </tr>
            </thead>

            <tbody>
              {filteredShifts.map(
                (shift) => (
                  <tr key={shift._id}>
                    <td>
                      {formatDate(
                        shift.started_at
                      )}
                    </td>

                    <td>
                      <strong>
                        {guardName(shift)}
                      </strong>
                    </td>

                    <td>
                      {displayValue(
                        shift.guard_id
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.department_abbreviation ||
                          shift.department_name
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.unit_name
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.shift_name
                      )}
                    </td>

                    <td>
                      {formatTime(
                        shift.started_at
                      )}
                    </td>

                    <td>
                      {formatTime(
                        shift.ended_at
                      )}
                    </td>

                    <td>
                      {formatDuration(
                        shift.started_at,
                        shift.ended_at
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.vehicle_number
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.start_mileage
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.end_mileage
                      )}
                    </td>

                    <td>
                      {calculateMiles(
                        shift.start_mileage,
                        shift.end_mileage
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.start_fuel_level
                      )}
                    </td>

                    <td>
                      {displayValue(
                        shift.end_fuel_level
                      )}
                    </td>

                    <td>
                      {shift.status ===
                      "active" ? (
                        <span className="badge bg-success">
                          ACTIVE
                        </span>
                      ) : (
                        <span className="badge bg-secondary">
                          ENDED
                        </span>
                      )}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="responder-shift-details-btn"
                        onClick={() => {
                            setSelectedShift(shift);
                            setLargeShiftPhotoUrl(null);
                            loadShiftPhotos(shift);
                            }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}


      {/* V1 API LIMIT NOTICE */}
      {shifts.length >= 250 && (
        <div
          className="alert alert-warning mt-3"
        >
          Showing the newest 250 responder
          shift sessions. Older history is not
          included in this view yet.
        </div>
      )}


      {/* SHIFT DETAILS */}
      {selectedShift && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{
            backgroundColor:
              "rgba(0,0,0,0.45)",
          }}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h4 className="modal-title mb-0">
                    Responder Shift Details
                  </h4>

                  <small className="text-muted">
                    {guardName(
                      selectedShift
                    )}{" "}
                    —{" "}
                    {formatDate(
                      selectedShift.started_at
                    )}
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() =>
                    setSelectedShift(null)
                  }
                />
              </div>


              <div className="modal-body">
                <h5>
                  Guard & Assignment
                </h5>

                <div className="row mb-4">
                  <div className="col-md-4">
                    <strong>
                      Guard:
                    </strong>{" "}
                    {guardName(
                      selectedShift
                    )}
                  </div>

                  <div className="col-md-4">
                    <strong>
                      Guard ID:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.guard_id
                    )}
                  </div>

                  <div className="col-md-4">
                    <strong>
                      Department:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.department_name
                    )}
                  </div>

                  <div className="col-md-4 mt-2">
                    <strong>
                      Unit:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.unit_name
                    )}
                  </div>

                  <div className="col-md-4 mt-2">
                    <strong>
                      Shift:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.shift_name
                    )}
                  </div>

                  <div className="col-md-4 mt-2">
                    <strong>
                      Partner:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.partner_name
                    )}
                  </div>
                </div>


                <hr />


                <h5>Shift</h5>

                <div className="row mb-4">
                  <div className="col-md-3">
                    <strong>
                      Time On:
                    </strong>
                    <br />
                    {formatDateTime(
                      selectedShift.started_at
                    )}
                  </div>

                  <div className="col-md-3">
                    <strong>
                      Time Off:
                    </strong>
                    <br />
                    {formatDateTime(
                      selectedShift.ended_at
                    )}
                  </div>

                  <div className="col-md-3">
                    <strong>
                      Duration:
                    </strong>
                    <br />
                    {formatDuration(
                      selectedShift.started_at,
                      selectedShift.ended_at
                    )}
                  </div>

                  <div className="col-md-3">
                    <strong>
                      Status:
                    </strong>
                    <br />

                    {selectedShift.status ===
                    "active"
                      ? "ACTIVE"
                      : "ENDED"}
                  </div>
                </div>


                <hr />


                <h5>Vehicle</h5>

                <div className="row mb-3">
                  <div className="col-md-3">
                    <strong>
                      Vehicle:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.vehicle_number
                    )}
                  </div>

                  <div className="col-md-3">
                    <strong>
                      Start Mileage:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.start_mileage
                    )}
                  </div>

                  <div className="col-md-3">
                    <strong>
                      End Mileage:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.end_mileage
                    )}
                  </div>

                  <div className="col-md-3">
                    <strong>
                      Miles Driven:
                    </strong>{" "}
                    {calculateMiles(
                      selectedShift.start_mileage,
                      selectedShift.end_mileage
                    )}
                  </div>

                  <div className="col-md-3 mt-3">
                    <strong>
                      Start Fuel:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.start_fuel_level
                    )}
                  </div>

                  <div className="col-md-3 mt-3">
                    <strong>
                      End Fuel:
                    </strong>{" "}
                    {displayValue(
                      selectedShift.end_fuel_level
                    )}
                  </div>
                </div>


                <div className="row mb-4">
                  <div className="col-md-6">
                    <strong>
                      Start Vehicle Condition
                    </strong>

                    <div className="border rounded p-2 mt-1">
                      {displayValue(
                        selectedShift.start_vehicle_condition
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <strong>
                      End Vehicle Condition
                    </strong>

                    <div className="border rounded p-2 mt-1">
                      {displayValue(
                        selectedShift.end_vehicle_condition
                      )}
                    </div>
                  </div>
                </div>


                <hr />


                <h5>
                  Vehicle Check Incident
                </h5>

                <p>
                  {displayValue(
                    selectedShift.vehicle_check_display_title,
                    "No linked Vehicle Check incident"
                  )}
                </p>


                <hr />


            <h5>Vehicle Photos</h5>

            {shiftPhotosLoading && (
            <div className="alert alert-light border">
                Loading secure vehicle photos...
            </div>
            )}

            {shiftPhotoError && (
            <div className="alert alert-danger">
                {shiftPhotoError}
            </div>
            )}

            <div className="row">
            <div className="col-md-6">
                <strong>Start Shift Photos</strong>

                <div className="mt-2">
                {selectedShift.photos_start?.length ? (
                    <div className="responder-shift-photo-grid">
                    {selectedShift.photos_start.map(
                        (photo, index) => {
                        const photoUrl =
                            shiftPhotoUrls[
                            photo.object_key
                            ];

                        return (
                            <div
                            key={
                                photo.object_key ||
                                `start-${index}`
                            }
                            className="responder-shift-photo-item"
                            >
                            {photoUrl ? (
                                <button
                                type="button"
                                className="responder-shift-photo-button"
                                onClick={() =>
                                    setLargeShiftPhotoUrl(
                                    photoUrl
                                    )
                                }
                                >
                                <img
                                    src={photoUrl}
                                    alt={`Start shift vehicle ${
                                    index + 1
                                    }`}
                                    className="responder-shift-photo-thumbnail"
                                />
                                </button>
                            ) : (
                                <div className="responder-shift-photo-placeholder">
                                Photo unavailable
                                </div>
                            )}
                            </div>
                        );
                        }
                    )}
                    </div>
                ) : (
                    <span className="text-muted">
                    No start photos
                    </span>
                )}
                </div>
            </div>

            <div className="col-md-6">
                <strong>End Shift Photos</strong>

                <div className="mt-2">
                {selectedShift.photos_end?.length ? (
                    <div className="responder-shift-photo-grid">
                    {selectedShift.photos_end.map(
                        (photo, index) => {
                        const photoUrl =
                            shiftPhotoUrls[
                            photo.object_key
                            ];

                        return (
                            <div
                            key={
                                photo.object_key ||
                                `end-${index}`
                            }
                            className="responder-shift-photo-item"
                            >
                            {photoUrl ? (
                                <button
                                type="button"
                                className="responder-shift-photo-button"
                                onClick={() =>
                                    setLargeShiftPhotoUrl(
                                    photoUrl
                                    )
                                }
                                >
                                <img
                                    src={photoUrl}
                                    alt={`End shift vehicle ${
                                    index + 1
                                    }`}
                                    className="responder-shift-photo-thumbnail"
                                />
                                </button>
                            ) : (
                                <div className="responder-shift-photo-placeholder">
                                Photo unavailable
                                </div>
                            )}
                            </div>
                        );
                        }
                    )}
                    </div>
                ) : (
                    <span className="text-muted">
                    No end photos
                    </span>
                )}
                </div>
              </div>
             </div>
            </div>


              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() =>
                    setSelectedShift(null)
                  }
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* LARGE SHIFT PHOTO VIEWER */}
{largeShiftPhotoUrl && (
  <div
    className="responder-shift-photo-viewer"
    role="dialog"
    aria-modal="true"
    aria-label="Vehicle photo"
    onClick={() =>
      setLargeShiftPhotoUrl(null)
    }
  >
    <div
      className="responder-shift-photo-viewer-content"
      onClick={(event) =>
        event.stopPropagation()
      }
    >
      <button
        type="button"
        className="responder-shift-photo-viewer-close"
        aria-label="Close photo"
        onClick={() =>
          setLargeShiftPhotoUrl(null)
        }
      >
        ×
      </button>

      <img
        src={largeShiftPhotoUrl}
        alt="Responder shift vehicle"
        className="responder-shift-photo-large"
      />
    </div>
  </div>
)}
    </div>
  );
}